// ==================== ENUMS ====================

export enum CategoryType {
  MATERIAL = 'MATERIAL',
  EQUIPMENT = 'EQUIPMENT',
  BOTH = 'BOTH',
}

export enum TransactionType {
  RECEIVE = 'RECEIVE',
  CONSUME = 'CONSUME',
  ADJUST = 'ADJUST',
  RESERVE = 'RESERVE',
  RELEASE_RESERVE = 'RELEASE_RESERVE',
  RETURN = 'RETURN',
  RECOVERY = 'RECOVERY',
  TRANSFER = 'TRANSFER',
  USE_AS_EQUIPMENT = 'USE_AS_EQUIPMENT',
}

export enum TransactionStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum AdjustmentReason {
  COUNT_CORRECTION = 'COUNT_CORRECTION',
  DAMAGED = 'DAMAGED',
  LOST = 'LOST',
  STOLEN = 'STOLEN',
  BROKEN = 'BROKEN',
  EXPIRED = 'EXPIRED',
  FOUND = 'FOUND',
  TRANSFER = 'TRANSFER',
  OTHER = 'OTHER',
}

export enum MaterialSource {
  WAREHOUSE = 'WAREHOUSE',
  MANUAL = 'MANUAL',
}

export enum TaskMaterialStatus {
  PLANNED = 'PLANNED',
  RESERVED = 'RESERVED',
  CONSUMED = 'CONSUMED',
  RETURNED = 'RETURNED',
}

export enum EquipmentStatus {
  AVAILABLE = 'AVAILABLE',
  IN_USE = 'IN_USE',
  MAINTENANCE = 'MAINTENANCE',
  RESERVED = 'RESERVED',
  RETIRED = 'RETIRED',
}

export enum EquipmentCondition {
  GOOD = 'GOOD',
  FAIR = 'FAIR',
  NEEDS_REPAIR = 'NEEDS_REPAIR',
}

export enum ReservationStatus {
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
}

// ==================== WAREHOUSE ====================

export interface Warehouse {
  warehouseId: string;
  name: string;
  description?: string;
  address?: string;
  lat?: number;
  long?: number;
  isActive: boolean;
  isDefault: boolean;
  organizationId: string;
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
}

export interface WarehouseCreateInput {
  name: string;
  description?: string;
  address?: string;
  lat?: number;
  long?: number;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface WarehouseUpdateInput {
  warehouseId: string;
  name?: string;
  description?: string;
  address?: string;
  lat?: number;
  long?: number;
  isActive?: boolean;
}

// ==================== MATERIAL CATEGORY ====================

export interface MaterialCategory {
  categoryId: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  type: CategoryType;
  isActive: boolean;
  sortOrder: number;
  isGlobal: boolean;
  calculatorType?: string;
  organizationId: string;
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
}

export interface MaterialCategoryCreateInput {
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  type?: CategoryType;
  isActive?: boolean;
  sortOrder?: number;
}

export interface MaterialCategoryUpdateInput {
  categoryId: string;
  name?: string;
  description?: string;
  icon?: string;
  color?: string;
  type?: CategoryType;
  isActive?: boolean;
  sortOrder?: number;
}

// ==================== MATERIAL CATALOG ====================

export interface MaterialCatalog {
  catalogId: string;
  name: string;
  sku?: string;
  description?: string;
  unit?: string;
  unitSystem?: string;
  averageCost: number;
  defaultPricePerUnit?: number;
  reorderPoint?: number;
  suggestedOrderQuantity?: number;
  defaultSupplier?: string;
  leadTimeDays?: number;
  isActive: boolean;
  categoryId?: string;
  category?: MaterialCategory;
  organizationId: string;
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
}

export interface MaterialCatalogCreateInput {
  name: string;
  sku?: string;
  description?: string;
  unit?: string;
  unitSystem?: string;
  averageCost?: number;
  defaultPricePerUnit?: number;
  reorderPoint?: number;
  suggestedOrderQuantity?: number;
  defaultSupplier?: string;
  leadTimeDays?: number;
  isActive?: boolean;
  categoryId?: string;
}

export interface MaterialCatalogUpdateInput {
  catalogId: string;
  name?: string;
  sku?: string;
  description?: string;
  unit?: string;
  unitSystem?: string;
  defaultPricePerUnit?: number;
  reorderPoint?: number;
  suggestedOrderQuantity?: number;
  defaultSupplier?: string;
  leadTimeDays?: number;
  isActive?: boolean;
  categoryId?: string;
}

// ==================== STOCK LEVEL ====================

export interface StockLevel {
  stockLevelId: string;
  quantityOnHand: number;
  quantityReserved: number;
  quantityAvailable: number; // Computed: onHand - reserved
  averageCost: number;
  catalogId: string;
  catalog?: MaterialCatalog;
  warehouseId: string;
  warehouse?: Warehouse;
  lastReceivedAt?: string | Date;
  lastConsumedAt?: string | Date;
  updatedAt?: string | Date;
}

export interface StockSummary {
  catalogId: string;
  catalogName: string;
  totalOnHand: number;
  totalReserved: number;
  totalAvailable: number;
  averageCost: number;
  warehouseCount: number;
}

// ==================== STOCK TRANSACTION ====================

export interface StockTransaction {
  transactionId: string;
  type: TransactionType;
  status: TransactionStatus;
  quantity: number;
  unitCost?: number;
  totalCost?: number;
  referenceNumber?: string;
  notes?: string;
  reason?: AdjustmentReason;
  supplier?: string;
  catalogId?: string;
  catalog?: MaterialCatalog;
  warehouseId?: string;
  warehouse?: Warehouse;
  toWarehouseId?: string;
  toWarehouse?: Warehouse;
  taskId?: string;
  task?: { taskId: string; name: string };
  projectId?: string;
  project?: { projectId: string; name: string };
  userId?: string;
  user?: { userId: string; firstName: string; lastName: string };
  organizationId: string;
  chargeToOrganizationId?: string;
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
}

// Transaction Input Types
export interface ReceiveStockInput {
  catalogId: string;
  warehouseId: string;
  quantity: number;
  unitCost: number;
  supplier?: string;
  referenceNumber?: string;
  notes?: string;
}

export interface ConsumeStockInput {
  catalogId: string;
  warehouseId: string;
  quantity: number;
  taskId: string;
  notes?: string;
}

export interface AdjustStockInput {
  catalogId: string;
  warehouseId: string;
  newQuantity: number; // Absolute target quantity after adjustment
  reason?: AdjustmentReason;
  notes: string; // Required for audit
}

export interface ReserveStockInput {
  catalogId: string;
  warehouseId: string;
  quantity: number;
  taskId?: string;
  notes?: string;
}

export interface ReturnStockInput {
  catalogId: string;
  warehouseId: string;
  quantity: number;
  taskId?: string;
  notes?: string;
}

export interface TransferStockInput {
  catalogId: string;
  fromWarehouseId: string;
  toWarehouseId: string;
  quantity: number;
  notes?: string;
}

export interface StockTransactionFilterInput {
  catalogId?: string;
  categoryId?: string;
  warehouseId?: string;
  taskId?: string;
  projectId?: string;
  userId?: string;
  type?: TransactionType;
  startDate?: string | Date;
  endDate?: string | Date;
  offset?: number;
  limit?: number;
}

export interface EquipmentUsageFilterInput {
  categoryId?: string;
  warehouseId?: string;
  assetId?: string;
  taskId?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  offset?: number;
  limit?: number;
}

// ==================== TASK MATERIAL ====================

export interface TaskMaterial {
  taskMaterialId: string;
  source: MaterialSource;
  status: TaskMaterialStatus;
  catalogId?: string;
  catalog?: MaterialCatalog;
  warehouseId?: string;
  warehouse?: Warehouse;
  name: string;
  supplier?: string;
  quantity: number;
  unit?: string;
  costPerUnit: number;
  pricePerUnit?: number;
  chargeToClient: boolean;
  leadTimeDays?: number;
  time?: string;
  taskId: string;
  transactionId?: string;
  transaction?: StockTransaction;
  organizationId: string;
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
}

export interface TaskMaterialManualInput {
  taskId: string;
  name: string;
  supplier?: string;
  quantity: number;
  unit?: string;
  costPerUnit: number;
  pricePerUnit?: number;
  chargeToClient?: boolean;
  time?: string;
}

export interface TaskMaterialWarehouseInput {
  taskId: string;
  catalogId: string;
  warehouseId: string;
  quantity: number;
  pricePerUnit?: number;
  chargeToClient?: boolean;
  skipReservation?: boolean;
}

export interface TaskMaterialPlannedInput {
  taskId: string;
  name: string;
  supplier?: string;
  quantity?: number;
  unit?: string;
  catalogId?: string;
  warehouseId?: string;
  costPerUnit?: number;
  pricePerUnit?: number;
  chargeToClient?: boolean;
  leadTimeDays?: number;
}

export interface TaskMaterialUpdateInput {
  taskMaterialId: string;
  quantity?: number;
  pricePerUnit?: number;
  chargeToClient?: boolean;
  leadTimeDays?: number;
}

export interface TaskMaterialReturnInput {
  taskMaterialId: string;
  quantity: number;
  notes?: string;
}

export interface TaskMaterialSummary {
  totalCost: number;
  totalPrice: number;
  warehouseMaterialsCount: number;
  manualMaterialsCount: number;
}

// ==================== TASK EQUIPMENT ====================

export enum EquipmentSource {
  WAREHOUSE = 'WAREHOUSE',
  MANUAL = 'MANUAL',
}

export enum TaskEquipmentStatus {
  PLANNED = 'PLANNED',
  RESERVED = 'RESERVED',
  IN_USE = 'IN_USE',
  RETURNED = 'RETURNED',
}

export interface TaskEquipment {
  taskEquipmentId: string;
  source: EquipmentSource;
  status: TaskEquipmentStatus;
  assetId?: string;
  asset?: EquipmentAsset;
  categoryId?: string;
  category?: MaterialCategory;
  warehouseId?: string;
  warehouse?: Warehouse;
  name: string;
  description?: string;
  estimatedHours?: number;
  costPerHour?: number;
  pricePerHour?: number;
  chargeToClient: boolean;
  taskId: string;
  usageId?: string;
  reservationId?: string;
  organizationId: string;
  totalCost: number;
  totalPrice: number;
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
}

export interface TaskEquipmentManualInput {
  taskId: string;
  name: string;
  description?: string;
  estimatedHours?: number;
  costPerHour?: number;
  pricePerHour?: number;
  chargeToClient?: boolean;
}

export interface TaskEquipmentWarehouseInput {
  taskId: string;
  assetId: string;
  startDate?: string | Date;
  endDate?: string | Date;
  notes?: string;
  chargeToClient?: boolean;
  costPerHour?: number;
  pricePerHour?: number;
  estimatedHours?: number;
}

export interface TaskEquipmentPlannedInput {
  taskId: string;
  name: string;
  description?: string;
  categoryId?: string;
  warehouseId?: string;
  estimatedHours?: number;
  costPerHour?: number;
  pricePerHour?: number;
  chargeToClient?: boolean;
}

export interface TaskEquipmentUpdateInput {
  taskEquipmentId: string;
  name?: string;
  description?: string;
  estimatedHours?: number;
  costPerHour?: number;
  pricePerHour?: number;
  chargeToClient?: boolean;
}

export interface TaskEquipmentAssignAssetInput {
  taskEquipmentId: string;
  assetId: string;
  startDate?: string | Date;
  endDate?: string | Date;
  notes?: string;
}

export interface TaskEquipmentReturnInput {
  taskEquipmentId: string;
  hoursUsed?: number;
  condition?: string;
  notes?: string;
  costPerHour?: number;
  pricePerHour?: number;
  chargeToClient?: boolean;
}

export interface TaskEquipmentSummary {
  totalCost: number;
  totalPrice: number;
  warehouseEquipmentCount: number;
  manualEquipmentCount: number;
}

export interface GetTaskEquipmentsResponse {
  getTaskEquipments: TaskEquipment[];
}

export interface GetTaskEquipmentResponse {
  getTaskEquipment: TaskEquipment;
}

export interface GetTaskEquipmentSummaryResponse {
  getTaskEquipmentSummary: TaskEquipmentSummary;
}

// ==================== EQUIPMENT ASSET ====================

export interface EquipmentAsset {
  assetId: string;
  name: string;
  assetCode?: string;
  description?: string;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  categoryId?: string;
  category?: MaterialCategory;
  status: EquipmentStatus;
  purchaseCost?: number;
  purchaseDate?: string | Date;
  hourlyRate?: number;
  clientHourlyRate?: number;
  warehouseId?: string;
  warehouse?: Warehouse;
  currentTaskId?: string;
  currentTask?: {
    taskId: string;
    name: string;
    projectId?: string;
    originalTaskId?: string;
    project?: {
      projectId: string;
      name: string;
    };
  };
  organizationId: string;
  maintenanceNotes?: string;
  lastMaintenanceDate?: string | Date;
  estimatedAvailableDate?: string | Date;
  reservationStartDate?: string | Date;
  reservationEndDate?: string | Date;
  reservations?: EquipmentReservation[];
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
}

export interface EquipmentAssetCreateInput {
  name: string;
  assetCode?: string;
  description?: string;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  categoryId?: string;
  purchaseCost?: number;
  purchaseDate?: string | Date;
  hourlyRate?: number;
  clientHourlyRate?: number;
  warehouseId?: string;
}

export interface EquipmentAssetUpdateInput {
  assetId: string;
  name?: string;
  assetCode?: string;
  description?: string;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  categoryId?: string;
  purchaseCost?: number;
  purchaseDate?: string | Date;
  hourlyRate?: number;
  clientHourlyRate?: number;
  warehouseId?: string;
}

export interface EquipmentCheckoutInput {
  assetId: string;
  taskId: string;
  startDate?: string | Date;
  endDate?: string | Date;
  notes?: string;
  chargeToClient?: boolean;
  costPerHour?: number;
  pricePerHour?: number;
}

export interface EquipmentReturnInput {
  assetId: string;
  hoursUsed?: number;
  condition: EquipmentCondition;
  notes?: string;
  chargeToClient?: boolean;
  costPerHour?: number;
  pricePerHour?: number;
}

export interface EquipmentMaintenanceInput {
  assetId: string;
  maintenanceNotes: string;
  estimatedAvailableDate?: string | Date;
}

export interface EquipmentReserveInput {
  assetId: string;
  taskId: string;
  startDate?: string | Date;
  endDate?: string | Date;
  notes?: string;
}

export interface EquipmentUpdateReservationInput {
  reservationId: string;
  taskId?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  notes?: string;
}

// ==================== EQUIPMENT RESERVATION ====================

export interface EquipmentReservation {
  reservationId: string;
  assetId: string;
  taskId: string;
  task?: {
    taskId: string;
    name: string;
    projectId?: string;
    project?: {
      projectId: string;
      name: string;
    };
  };
  asset?: {
    assetId: string;
    name: string;
    assetCode?: string;
  };
  startDate: string | Date;
  endDate: string | Date;
  notes?: string;
  status: ReservationStatus;
  isCheckoutReservation?: boolean;
  organizationId?: string;
  createdByUserId?: string;
  createdAt?: string | Date;
}

// ==================== EQUIPMENT USAGE ====================

export interface EquipmentUsage {
  usageId: string;
  assetId: string;
  asset?: EquipmentAsset;
  taskId?: string;
  task?: {
    taskId: string;
    name: string;
    projectId?: string;
    project?: {
      projectId: string;
      name: string;
    };
  };
  checkoutDate: string | Date;
  returnDate?: string | Date;
  expectedReturnDate?: string | Date;
  hoursUsed?: number;
  chargeToClient?: boolean;
  costPerHour?: number;
  pricePerHour?: number;
  condition?: EquipmentCondition;
  notes?: string;
  checkoutNotes?: string;
  checkoutUserId?: string;
  checkoutUser?: { userId: string; firstName: string; lastName: string };
  returnUserId?: string;
  returnUser?: { userId: string; firstName: string; lastName: string };
  createdAt?: string | Date;
}

// ==================== GRAPHQL RESPONSE TYPES ====================

export interface GetWarehousesResponse {
  getWarehouses: Warehouse[];
}

export interface GetMaterialCategoriesResponse {
  getMaterialCategories: MaterialCategory[];
}

export interface GetMaterialCatalogResponse {
  getMaterialCatalog: MaterialCatalog[];
}

export interface GetStockLevelsResponse {
  getStockLevels: StockLevel[];
}

export interface GetStockTransactionsResponse {
  getStockTransactions: StockTransaction[];
}

export interface GetTaskMaterialsResponse {
  getTaskMaterials: TaskMaterial[];
}

export interface GetEquipmentAssetsResponse {
  getEquipmentAssets: EquipmentAsset[];
}

export interface GetEquipmentUsageHistoryResponse {
  getEquipmentUsageHistory: EquipmentUsage[];
}

export interface GetAllEquipmentUsageHistoryResponse {
  getAllEquipmentUsageHistory: EquipmentUsage[];
}

export interface DeleteResultResponse {
  affected: number;
}

// ==================== LEAD TIME ALERTS ====================

export type LeadTimeAlertLevel = 'OVERDUE' | 'URGENT' | 'UPCOMING';

export interface LeadTimeAlert {
  taskMaterialId: string;
  materialName: string;
  taskName: string;
  taskId: string;
  projectName?: string;
  projectId?: string;
  leadTimeDays: number;
  taskStartDate: string | Date;
  orderByDate: string | Date;
  daysUntilDeadline: number;
  alertLevel: LeadTimeAlertLevel;
  quantity: number;
  unit?: string;
  supplier?: string;
  catalogId?: string;
  warehouseId?: string;
}

export interface LeadTimeAlertCounts {
  overdue: number;
  urgent: number;
  upcoming: number;
  total: number;
}

export interface GetLeadTimeAlertsResponse {
  getLeadTimeAlerts: LeadTimeAlert[];
}

export interface GetLeadTimeAlertCountsResponse {
  getLeadTimeAlertCounts: LeadTimeAlertCounts;
}
